import React from "react"
import PropTypes from "prop-types"
import { Container, Title } from "@bw/bits"
import styled, { css } from "styled-components"
import { breakpoints } from "../../../theme"
import { VideoPlay } from "@bw/icons"
import { motion } from "framer-motion"

const Order = styled.div`
  display: flex;
  flex-direction: column-reverse;
  position: relative;

  @media (min-width: ${breakpoints.small}px) {
    flex-direction: row;
    padding-top: 65px;
    margin-bottom: 100px;
  }

  .title {
    position: relative;
    z-index: 3;
  }

  ${props =>
    props.$isPharmaciePlus
      ? css`
          .level {
            > :first-child {
              margin-right: 0;
            }
            > * {
              letter-spacing: 1px;
            }
          }
        `
      : null}
`
const Image = styled.div`
  position: relative;
  order: 1;
  z-index: 0;
  background-size: cover;
  width: 95vw;
  height: 234px;
  align-self: end;
  display: flex;
  margin: 0 0 20px;
  &:before {
    content: "";
    display: block;
    position: absolute;
    background-color: var(--page-color-rgba);
    inset: 0px;
    z-index: 1;
  }

  @media (min-width: ${breakpoints.small}px) {
    width: 40vw;
    height: 40vh;
    top: 0;
    right: 0;
    position: absolute;
  }

  @media (min-width: ${breakpoints.medium}px) {
    margin: 0;
    width: calc(50vw - 25px);
    height: 55vh;
  }

  @media (min-width: ${breakpoints.large}px) {
    height: 586px;
  }

  .gatsby-image-wrapper {
    height: 100%;
  }

  img {
    object-position: ${props => props.focus};
  }
`

const Color = styled.div`
  padding: 45px 20px;
  transform: translate(-15px, -35px);
  max-width: 549px;
  font-weight: 300;
  font-size: 16px;
  line-height: 189.3%;
  color: var(--font-color);
  z-index: 1;
  position: relative;
  background-color: var(--page-color);

  @media (min-width: ${breakpoints.small}px) {
    transform: translate(-15px, -35px);
    padding: 50px 20px;
    max-width: 400px;
  }

  @media (min-width: ${breakpoints.medium}px) {
    transform: translate(-70px, -44px);
    padding: 70px 35px 70px 70px;
    overflow: visible;
    max-width: 452px;
  }

  @media (min-width: ${breakpoints.large}px) {
    transform: translate(-70px, -53px);
    max-width: 549px;
  }
`

const Plus = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;

  > svg {
    position: absolute;
    left: -100px;
    bottom: -40px;
    color: #fff;
    opacity: 0.3;
  }

  @media (min-width: ${breakpoints.medium}px) {
    display: none;
  }
`

const Buttons = styled.div`
  position: absolute;
  bottom: 0;
  max-width: 100%;
  display: flex;
  gap: 20px;
  transform: translateY(50%);
  z-index: 4;
}
`

const Links = styled.div`
  margin-top: 25px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 18px;  
  
  * {
    font-size: 16px;
  }

  @media (min-width: ${breakpoints.small}px) {
    margin-top: 25px;
  }

  @media (min-width: ${breakpoints.medium}px) {
    margin-top: 35px;
  }
  @media (min-width: ${breakpoints.large}px) {
    margin-top: 85px;
  }
`

const Video = styled.video`
  object-fit: cover;
  width: 100%;
  height: 100%;
  align-self: end;
  display: flex;
`

const PlayWrapper = styled.div`
  align-self: center;
  justify-self: center;
  left: 50%;
  position: absolute;
  transform: translate(-50%);
`

const Play = styled.div`
  width: 74px;
  height: 74px;
`

const variants = {
  open: { opacity: 1, display: "flex", justifyContent: "flex-end" },
  closed: { opacity: 0, transitionEnd: { display: "none" } },
}

const innerVariants = {
  open: { opacity: 1, display: "flex" },
  closed: { opacity: 0, transitionEnd: { display: "none" } },
}

const Layer = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 9;
`

const InnerLayer = styled(motion.div)`
  background-color: #111;
  width: 100%;
  margin: auto;
  padding: 0;
  aspect-ratio: 16 / 9;
  width: 90vw;
  max-width: 800px;
`

const VideoWapper = styled.div`
  position: relative;
  width: 95vw;
  height: 234px;
  align-self: end;
  display: flex;
  margin: 0 0 20px;
  &:before {
    content: "";
    display: block;
    position: absolute;
    background-color: var(--page-color-rgba);
    inset: 0px;
  }

  @media (min-width: ${breakpoints.small}px) {
    width: 40vw;
    height: 40vh;
    top: 0;
    right: 0;
    position: absolute;
  }

  @media (min-width: ${breakpoints.medium}px) {
    margin: 0;
    width: calc(50vw - 25px);
    height: 55vh;
  }

  @media (min-width: ${breakpoints.large}px) {
    height: 586px;
  }
`

const Hero = ({
  heroTitle,
  heroSuptitle,
  containerSize,
  excerpt,
  buttons,
  links,
  image,
  focus,
  video,
  videoPopup,
}) => {
  const [show, setShow] = React.useState(false)
  const layerRef = React.useRef(null)
  const videoRef = React.useRef(null)

  const handleEsc = event => {
    if (event.keyCode === 27) {
      setShow(false)
    }
  }

  const closeOnOutsideClick = (e, ref) => {
    if (e.target === ref.current) {
      setShow(false)
    }
  }

  React.useEffect(() => {
    window.addEventListener("keydown", handleEsc)

    return () => {
      window.removeEventListener("keydown", handleEsc)
    }
  }, [])

  React.useEffect(() => {
    if (videoPopup && videoRef.current) {
      if (show) {
        videoRef.current.play()
      } else {
        videoRef.current.pause()
      }
    }
  }, [videoRef, videoPopup, show])

  return (
    <Order
      $isPharmaciePlus={heroTitle === "plus" && heroSuptitle === "pharmacie"}
    >
      <Container size="medium">
        <Title title={heroTitle} suptitle={heroSuptitle} size="h1" level={1} />
        <Color>
          <p>{excerpt}</p>

          <Plus>
            <svg width="358" height="358">
              <use xlinkHref="#plus" />
            </svg>
          </Plus>
          {buttons && <Buttons>{buttons}</Buttons>}
        </Color>
        {links && <Links>{links}</Links>}
      </Container>
      {video ? (
        <VideoWapper>
          <Video autoPlay muted loop>
            <source src={video} type="video/mp4" />
          </Video>
          <PlayWrapper>
            {videoPopup && (
              <Play
                onClick={() => setShow(true)}
                role="button"
                aria-label="Voir la vidéo"
              >
                <VideoPlay />
              </Play>
            )}
          </PlayWrapper>
        </VideoWapper>
      ) : (
        <Image focus={focus}>{image}</Image>
      )}

      {videoPopup && (
        <Layer
          animate={show ? "open" : "closed"}
          variants={variants}
          transition={{ duration: 0.4 }}
          initial={false}
          ref={layerRef}
          onClick={e => closeOnOutsideClick(e, layerRef)}
        >
          <InnerLayer
            animate={show ? "open" : "closed"}
            variants={innerVariants}
            transition={{ duration: 0.6 }}
            initial={false}
          >
            <video controls autoPlay ref={videoRef}>
              <source src={videoPopup} type="video/mp4" />
            </video>
          </InnerLayer>
        </Layer>
      )}
    </Order>
  )
}

Hero.propTypes = {
  video: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
    })
  ),
  heroTitle: PropTypes.string,
  heroSuptitle: PropTypes.string,
  excerpt: PropTypes.string,
  buttons: PropTypes.arrayOf(PropTypes.element),
  links: PropTypes.arrayOf(PropTypes.element),
  image: PropTypes.element.isRequired,
  focus: PropTypes.string,
}

Hero.defaultProps = {
  focus: "center",
}

export { Hero }
